import {
  Component,
  PLATFORM_ID,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd, ActivationEnd } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { AuthenticationService } from '@libs/authentication/services';
import { APP_CONFIG } from '@libs/configs';
import { AdobeService } from '@libs/services';
import { UserLifecycleService } from '@libs/events';
import { UserDataService } from '@libs/services/user-data';
import { MENU } from './navigation.menu';
import posthog from 'posthog-js';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

const posthogCaptureRouterEvents = (router: Router, destroyRef: DestroyRef) =>
  router.events
    .pipe(
      filter((e) => e instanceof NavigationEnd),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(() => posthog.capture('$pageview'));

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private appConfig = inject<any>(APP_CONFIG);
  protected readonly menu = MENU;
  protected readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly hideLayout = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(
        (event: NavigationEnd) =>
          (event.url.indexOf('events/') !== -1 ||
            event.url.indexOf('candidates/') !== -1) &&
          event.url.indexOf('interview/') !== -1,
      ),
    ),
    { initialValue: false },
  );

  protected readonly enableTopNav = this.appConfig.enableTopNav;

  constructor(
    private auth: AuthenticationService,
    private adobeService: AdobeService,
    private userDataService: UserDataService,
    private userLifecycleService: UserLifecycleService,
  ) {
    posthog.init('phc_A6djNElHQil1OQlQNnrDzQtCog5vH3fJoBRZ2qnyJsa', {
      api_host: 'https://a.snagajob.com',
      autocapture: false,
      person_profiles: 'identified_only',
      custom_campaign_params: ['ref'],
      capture_pageview: false,
      capture_pageleave: true,
      session_recording: {
        maskInputFn(text, element) {
          return element.dataset['record'] === 'true'
            ? text
            : '*'.repeat(text.length);
        },
      },
    });
  }

  ngOnInit() {
    this.auth.user$
      .pipe(
        filter((user) => user.loggedIn),
        take(1),
      )
      .subscribe(() => {
        this.userLifecycleService.init();
      });

    if (this.isBrowser) {
      this.userDataService.setUserType();
    }

    posthogCaptureRouterEvents(this.router, this.destroyRef);
    this.initAuthSubscriptions();
  }

  initAuthSubscriptions(): void {
    const perm$ = this.auth.permission$.pipe(
      filter((perm) => !!perm?.accountId),
    );
    const auth$ = this.auth.user$.pipe(filter((user) => !!user?.accountId));
    const account$ = this.auth.account$.pipe(
      filter((account) => !!account?.id),
    );

    combineLatest([perm$, auth$, account$]).subscribe(
      ([perm, auth, account]) => {
        const isImpersonatedUser = auth.jwt.prt === 'sajcorp';
        const userId = perm.id;

        posthog.identify(userId, {
          member_id: perm.memberId,
          account_id: perm.accountId,
          account_name: account.name,
          account_tier: account.accountTier,
          account_type: account.accountType,
          email: perm.emailAddress,
          name: `${perm.firstName} ${perm.lastName}`,
          create_date: perm.createDate,
          roles: perm?.roles?.join(','),
        });

        posthog.group('employer_account', account.id, {
          name: account.name,
          tier: account.accountTier,
          type: account.accountType,
          review_status: account?.review?.reviewStatus,
          create_date: account.createDate,
        });
        this.userDataService.setEcommStatus(account.isHubSelfSignup);
        this.adobeService.configureAdobeForUser(
          isImpersonatedUser,
          perm.accountId,
          perm.memberId,
          perm.roles,
          true,
          account.isHubSelfSignup,
        );
      },
    );
  }
}
